import {
  useEffectWithController,
  useLoadableState,
  usePromise
} from '@carvana/merch-react-hooks';
import { useContext, useEffect, useRef, useState } from 'react';
import SortOption from '~/constants/common/SortOption';
import DeviceContext from '~/context/common/DeviceContext';
import UserContext, { useIsLoggedIn } from '~/context/common/UserContext';
import DeviceDetectionContext from '~/context/giveaway/DeviceDetectionContext';
import { RecommendedVehicle } from '~/contracts/vdp';
import { RequestedFeature } from '~/services/common/contracts';
import AppliedFilters from '~/services/common/contracts/AppliedFilters';
import fetchInventory from '~/services/common/fetchInventory';
import { BrowserCookies } from '~/utilities';
import generateErrorLogger from '~/utilities/errorHandlers';

function useGetVehicles(
  filters: Partial<AppliedFilters>,
  fetchTrigger: boolean
) {
  const isLoggedIn = useIsLoggedIn();
  const { deviceType } = useContext(DeviceContext);
  const {
    deviceDetection: {
      data: { browserName, model },
      isLoaded: isDeviceDetectionLoaded
    }
  } = useContext(DeviceDetectionContext);
  const { isMobile } = deviceType;
  const {
    favorites: { data: favorites = [] },
    location: {
      data: { zip = '' }
    }
  } = useContext(UserContext);
  const {
    data: inventory,
    error,
    isLoaded,
    isLoading,
    setData,
    setError,
    setLoading
  } = useLoadableState<RecommendedVehicle[]>([]);
  const mounted = usePromise();
  const [shouldFetch, setShouldFetch] = useState(fetchTrigger);
  const favoritesRef = useRef<number[]>([]);
  const isMobileRef = useRef(isMobile);

  useEffect(() => {
    if (fetchTrigger) {
      setShouldFetch(fetchTrigger);
    }
  }, [fetchTrigger, setLoading]);

  useEffect(() => {
    const favoritesIds = favorites.map(favorite => favorite.vehicleId);
    setData(prevVehicles =>
      prevVehicles.map(vehicle => ({
        ...vehicle,
        isFavorited: favoritesIds.includes(vehicle.vehicleId)
      }))
    );
    favoritesRef.current = favoritesIds;
  }, [favorites, setData]);

  useEffect(() => {
    favoritesRef.current = favorites.map(favorite => favorite.vehicleId);
  }, [favorites]);

  useEffect(() => {
    isMobileRef.current = isMobile;
  }, [isMobile]);

  useEffectWithController(
    controller => {
      if (!zip || !isDeviceDetectionLoaded || !shouldFetch) return;

      const requestedFeaturesToUse = new Array<RequestedFeature>().concat([
        'EarliestAcquisitionBoosting',
        'LocationBasedPrefiltering',
        'Personalization'
      ]);

      async function fetchVehicles() {
        setLoading();
        const { data } = await fetchInventory(
          {
            analyticsData: {
              browser: browserName ?? '',
              clientId: 'used_ev_tax_credit_ui',
              deviceName: model ?? '',
              isMobileDevice: isMobileRef.current,
              referrer: document.referrer
            },
            browserCookieId: BrowserCookies.get('BrowserCookieId') ?? '',
            filters: {
              ...filters
            },
            pagination: { page: 1, pageSize: 12 },
            requestedFeatures: requestedFeaturesToUse,
            sortBy: SortOption.MostPopular,
            zip5: zip
          },
          controller
        );

        if (data.inventory.vehicles.length === 0) return [];

        return data.inventory.vehicles;
      }

      mounted(fetchVehicles())
        .then(vehicles => {
          if (vehicles.length === 0) throw new Error('No vehicles found');
          setData(
            vehicles.map<RecommendedVehicle>(vehicle => ({
              ...vehicle,
              isFavorited: favoritesRef.current.includes(vehicle.vehicleId)
            }))
          );
        })
        .catch(
          generateErrorLogger('Get Ev vehicles', {}, { callback: setError })
        );
    },
    [
      browserName,
      filters,
      isDeviceDetectionLoaded,
      model,
      mounted,
      setData,
      setError,
      setLoading,
      shouldFetch,
      zip,
      isLoggedIn
    ]
  );

  return {
    error,
    isLoading,
    isLoaded,
    vehicles: inventory
  };
}

export default useGetVehicles;
